import * as THREE from "three/webgpu";
import type { CartographyLabelPlacement, CartographyLabelTier, NodeStatus } from "../../domain/mesh";
import type { PresentedNode, PresentedOrbital } from "../../presentation/mesh";
import { labelVisibility, type SemanticZoomState } from "../../experience/semanticZoom";
import {
  createNodeLabelTexture,
  createOrbitalLabelTexture,
  type LabelHierarchy,
  type TextureSpec,
} from "./sceneLabelTextures";
import { LABEL_OPACITY_RESPONSE, LABEL_SIZE_RESPONSE, labelTransitionFactor } from "./LabelMotion";

const NODE_LABEL_GAP_PX = 8;
const ORBITAL_LABEL_GAP_PX = 6;
const LABEL_INTERACTION_PADDING_PX = 10;

type LabelKind = "node" | "orbital";

type LabelRecord = {
  id: string;
  kind: LabelKind;
  parentId?: string;
  anchor: THREE.Vector3;
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  texture: THREE.CanvasTexture;
  widthPx: number;
  heightPx: number;
  displayWidthPx: number;
  displayHeightPx: number;
  displayOpacity: number;
  placement: CartographyLabelPlacement;
  offsetX: number;
  offsetY: number;
  baseScale: number;
  tier: CartographyLabelTier;
  label: string;
  eyebrow: string;
  status: NodeStatus | null;
  detailed: boolean;
  centeredOnAnchor: boolean;
  hierarchy: LabelHierarchy;
  visible: boolean;
  screenLeft: number;
  screenTop: number;
  screenRight: number;
  screenBottom: number;
};

export type LabelPick = {
  entityId: string;
  entityKind: LabelKind;
  parentId: string | null;
};

export class LabelLayer {
  private readonly records = new Map<string, LabelRecord>();
  private readonly cameraPosition = new THREE.Vector3();
  private readonly cameraRight = new THREE.Vector3();
  private readonly cameraUp = new THREE.Vector3();
  private readonly projection = new THREE.Vector3();
  private readonly labelOffset = new THREE.Vector2();

  constructor(private readonly scene: THREE.Scene) {}

  rebuild(nodes: PresentedNode[], orbitals: PresentedOrbital[], anchors: Map<string, THREE.Vector3>) {
    this.clear();
    for (const node of nodes) {
      const anchor = anchors.get(node.id);
      if (!anchor) continue;
      this.records.set(node.id, this.createNodeLabel(node, anchor));
    }
    for (const orbital of orbitals) {
      const anchor = anchors.get(orbital.id);
      if (!anchor) continue;
      this.records.set(orbital.id, this.createOrbitalLabel(orbital, anchor));
    }
  }

  updateCopy(nodes: PresentedNode[], orbitals: PresentedOrbital[]) {
    for (const node of nodes) {
      const record = this.records.get(node.id);
      if (!record) continue;
      record.hierarchy = this.nodeHierarchy(node);
      record.tier = node.cartography.labelTier;
      record.label = node.label;
      record.eyebrow = node.eyebrow;
      record.status = node.status;
      record.detailed = false;
      const next = createNodeLabelTexture(node.label, node.eyebrow, node.cartography.labelTier, record.hierarchy);
      this.swapTexture(record, next);
      if (!record.centeredOnAnchor) {
        record.placement = node.cartography.labelPlacement;
        record.offsetX = node.cartography.labelOffsetX ?? 0;
        record.offsetY = node.cartography.labelOffsetY ?? 0;
      }
      record.baseScale = this.nodeBaseScale(node, record.hierarchy);
      this.applySpriteCenter(record);
    }
    for (const orbital of orbitals) {
      const record = this.records.get(orbital.id);
      if (!record) continue;
      record.label = orbital.label;
      record.eyebrow = orbital.eyebrow;
      record.status = orbital.status;
      this.swapTexture(record, createOrbitalLabelTexture(orbital.label, orbital.eyebrow));
      this.applySpriteCenter(record);
    }
  }

  update(
    camera: THREE.PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    delta: number,
    focusedNodeId: string | null,
    focusedOrbitalId: string | null,
    hoveredId: string | null,
    related: ReadonlySet<string>,
    semanticZoom: SemanticZoomState,
    accessibleMode: boolean,
  ) {
    camera.getWorldPosition(this.cameraPosition);
    this.cameraRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
    this.cameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
    const viewportHeightSafe = Math.max(viewportHeight, 1);
    const worldPerPixelScale =
      (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5)) / viewportHeightSafe;
    const aspectCompensation = Math.max(viewportWidth / viewportHeightSafe, 0.65);
    const sizeTransition = labelTransitionFactor(delta, LABEL_SIZE_RESPONSE);
    const opacityTransition = labelTransitionFactor(delta, LABEL_OPACITY_RESPONSE);

    for (const record of this.records.values()) {
      const selected = record.id === focusedNodeId || record.id === focusedOrbitalId;
      const hovered = record.id === hoveredId;
      const isRelated = related.has(record.id);
      const dim = focusedNodeId !== null && !selected && !isRelated && record.parentId !== focusedNodeId;
      const shouldDetail = record.kind === "node" && semanticZoom.level >= 3 && (selected || isRelated);
      if (shouldDetail !== record.detailed) this.setNodeDetail(record, shouldDetail);
      const orbitalVisible =
        record.kind === "orbital"
          ? record.parentId === focusedNodeId && (selected || hovered || semanticZoom.level >= 4)
          : true;
      const semanticAlpha =
        record.kind === "orbital"
          ? semanticZoom.level >= 4
            ? 1
            : selected || hovered
              ? 1
              : 0
          : labelVisibility(record.kind, record.hierarchy, semanticZoom, selected, isRelated, orbitalVisible);
      record.visible = semanticAlpha > 0.04;
      record.sprite.visible = record.visible;
      if (!record.visible) {
        record.displayOpacity = 0;
        record.material.opacity = 0;
        continue;
      }

      const distance = Math.max(this.cameraPosition.distanceTo(record.anchor), 0.001);
      const worldPerPixel = worldPerPixelScale * distance;
      const emphasis = (selected ? 1.04 : hovered ? 1.015 : 1) * (accessibleMode ? 1.08 : 1);
      const targetWidthScreenPx = record.widthPx * record.baseScale * emphasis;
      const targetHeightScreenPx = record.heightPx * record.baseScale * emphasis;
      record.displayWidthPx += (targetWidthScreenPx - record.displayWidthPx) * sizeTransition;
      record.displayHeightPx += (targetHeightScreenPx - record.displayHeightPx) * sizeTransition;
      const offset = this.labelCenterOffsetPx(record, record.displayWidthPx, record.displayHeightPx, this.labelOffset);
      record.sprite.position
        .copy(record.anchor)
        .addScaledVector(this.cameraRight, offset.x * worldPerPixel)
        .addScaledVector(this.cameraUp, -offset.y * worldPerPixel);
      record.sprite.scale.set(record.displayWidthPx * worldPerPixel, record.displayHeightPx * worldPerPixel, 1);
      let targetOpacity = dim
        ? 0.14
        : selected
          ? record.hierarchy === "secondary"
            ? 0.82
            : 0.94
          : record.hierarchy === "secondary"
            ? 0.62
            : 0.84;
      targetOpacity *= semanticAlpha;
      if (accessibleMode) targetOpacity = Math.max(targetOpacity, record.hierarchy === "secondary" ? 0.58 : 0.82);
      if (aspectCompensation < 0.8 && record.kind === "node") targetOpacity *= 0.96;
      record.displayOpacity += (targetOpacity - record.displayOpacity) * opacityTransition;
      record.material.opacity = record.displayOpacity;
      record.sprite.renderOrder = selected ? 42 : record.kind === "node" ? 38 : 36;
      this.updateScreenBounds(
        record,
        camera,
        viewportWidth,
        viewportHeight,
        record.displayWidthPx,
        record.displayHeightPx,
      );
    }
  }

  pick(screenX: number, screenY: number, interactionPaddingScale = 1) {
    let best: LabelRecord | null = null;
    for (const record of this.records.values()) {
      if (!record.visible) continue;
      const paddingBase = record.centeredOnAnchor ? LABEL_INTERACTION_PADDING_PX * 1.2 : LABEL_INTERACTION_PADDING_PX;
      const padding = paddingBase * interactionPaddingScale;
      if (
        screenX < record.screenLeft - padding ||
        screenX > record.screenRight + padding ||
        screenY < record.screenTop - padding ||
        screenY > record.screenBottom + padding
      ) {
        continue;
      }
      if (!best) {
        best = record;
        continue;
      }
      const bestArea = (best.screenRight - best.screenLeft) * (best.screenBottom - best.screenTop);
      const candidateArea = (record.screenRight - record.screenLeft) * (record.screenBottom - record.screenTop);
      if (record.centeredOnAnchor || candidateArea < bestArea) best = record;
    }

    if (!best) return null;
    return {
      entityId: best.id,
      entityKind: best.kind,
      parentId: best.parentId ?? null,
    } satisfies LabelPick;
  }

  private createNodeLabel(node: PresentedNode, anchor: THREE.Vector3): LabelRecord {
    const hierarchy = this.nodeHierarchy(node);
    const spec = createNodeLabelTexture(node.label, node.eyebrow, node.cartography.labelTier, hierarchy);
    const material = new THREE.SpriteMaterial({
      map: spec.texture,
      color: hierarchy === "secondary" ? new THREE.Color(0.68, 0.72, 0.8) : new THREE.Color(0.88, 0.91, 0.98),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.copy(anchor);
    sprite.userData.entityId = node.id;
    sprite.userData.entityKind = "node";
    this.scene.add(sprite);

    const centeredOnAnchor = node.archetype === "black-hole";
    const baseScale = this.nodeBaseScale(node, hierarchy);
    const record: LabelRecord = {
      id: node.id,
      kind: "node",
      anchor,
      sprite,
      material,
      texture: spec.texture,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
      displayWidthPx: spec.widthPx * baseScale,
      displayHeightPx: spec.heightPx * baseScale,
      displayOpacity: 0.84,
      placement: centeredOnAnchor ? "south" : node.cartography.labelPlacement,
      offsetX: centeredOnAnchor ? 0 : (node.cartography.labelOffsetX ?? 0),
      offsetY: centeredOnAnchor ? 0 : (node.cartography.labelOffsetY ?? 0),
      baseScale,
      tier: node.cartography.labelTier,
      label: node.label,
      eyebrow: node.eyebrow,
      status: node.status,
      detailed: false,
      centeredOnAnchor,
      hierarchy,
      visible: true,
      screenLeft: 0,
      screenTop: 0,
      screenRight: 0,
      screenBottom: 0,
    };
    this.applySpriteCenter(record);
    return record;
  }

  private createOrbitalLabel(orbital: PresentedOrbital, anchor: THREE.Vector3): LabelRecord {
    const spec = createOrbitalLabelTexture(orbital.label, orbital.eyebrow);
    const material = new THREE.SpriteMaterial({
      map: spec.texture,
      color: new THREE.Color(0.64, 0.68, 0.76),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.copy(anchor);
    sprite.visible = false;
    sprite.userData.entityId = orbital.id;
    sprite.userData.entityKind = "orbital";
    sprite.userData.parentId = orbital.parentId;
    this.scene.add(sprite);

    const record: LabelRecord = {
      id: orbital.id,
      kind: "orbital",
      parentId: orbital.parentId,
      anchor,
      sprite,
      material,
      texture: spec.texture,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
      displayWidthPx: spec.widthPx * 0.84,
      displayHeightPx: spec.heightPx * 0.84,
      displayOpacity: 0,
      placement: "south-east",
      offsetX: 10,
      offsetY: 8,
      baseScale: 0.84,
      tier: "minor",
      label: orbital.label,
      eyebrow: orbital.eyebrow,
      status: orbital.status,
      detailed: false,
      centeredOnAnchor: false,
      hierarchy: "secondary",
      visible: false,
      screenLeft: 0,
      screenTop: 0,
      screenRight: 0,
      screenBottom: 0,
    };
    this.applySpriteCenter(record);
    return record;
  }

  private nodeHierarchy(node: PresentedNode): LabelHierarchy {
    if (node.archetype === "black-hole") return "singularity";
    return node.kind === "system" ? "primary" : "secondary";
  }

  private nodeBaseScale(node: PresentedNode, hierarchy: LabelHierarchy) {
    if (hierarchy === "singularity") return 0.78;
    const tierScale = node.cartography.labelTier === "hero" ? 1 : 0.96;
    return hierarchy === "primary" ? tierScale : tierScale * 0.88;
  }

  private setNodeDetail(record: LabelRecord, detailed: boolean) {
    record.detailed = detailed;
    if (record.kind !== "node") return;
    const meta = detailed && record.status ? `${record.eyebrow} · ${record.status.toUpperCase()}` : record.eyebrow;
    this.swapTexture(record, createNodeLabelTexture(record.label, meta, record.tier, record.hierarchy));
  }

  private applySpriteCenter(record: LabelRecord) {
    record.sprite.center.set(0.5, 0.5);
  }

  private labelCenterOffsetPx(record: LabelRecord, widthPx: number, heightPx: number, target: THREE.Vector2) {
    if (record.centeredOnAnchor) return target.set(0, 0);
    const gap = record.kind === "node" ? NODE_LABEL_GAP_PX : ORBITAL_LABEL_GAP_PX;
    const offsetX = record.offsetX + gap;
    const offsetY = record.offsetY + gap;

    switch (record.placement) {
      case "north":
        return target.set(record.offsetX, -(heightPx * 0.5 + offsetY));
      case "east":
        return target.set(widthPx * 0.5 + offsetX, -record.offsetY);
      case "west":
        return target.set(-(widthPx * 0.5 + offsetX), -record.offsetY);
      case "south-east":
        return target.set(widthPx * 0.5 + offsetX, heightPx * 0.5 + offsetY);
      case "south-west":
        return target.set(-(widthPx * 0.5 + offsetX), heightPx * 0.5 + offsetY);
      case "north-east":
        return target.set(widthPx * 0.5 + offsetX, -(heightPx * 0.5 + offsetY));
      case "north-west":
        return target.set(-(widthPx * 0.5 + offsetX), -(heightPx * 0.5 + offsetY));
      case "south":
      default:
        return target.set(record.offsetX, heightPx * 0.5 + offsetY);
    }
  }

  private updateScreenBounds(
    record: LabelRecord,
    camera: THREE.PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    widthScreenPx: number,
    heightScreenPx: number,
  ) {
    this.projection.copy(record.sprite.position).project(camera);
    const centerX = (this.projection.x + 1) * 0.5 * viewportWidth;
    const centerY = (1 - this.projection.y) * 0.5 * viewportHeight;

    record.screenLeft = centerX - widthScreenPx * 0.5;
    record.screenRight = centerX + widthScreenPx * 0.5;
    record.screenTop = centerY - heightScreenPx * 0.5;
    record.screenBottom = centerY + heightScreenPx * 0.5;
  }

  private swapTexture(record: LabelRecord, next: TextureSpec) {
    record.texture.dispose();
    record.texture = next.texture;
    record.material.map = next.texture;
    record.material.needsUpdate = true;
    record.widthPx = next.widthPx;
    record.heightPx = next.heightPx;
  }

  clear() {
    for (const record of this.records.values()) {
      this.scene.remove(record.sprite);
      record.texture.dispose();
      record.material.dispose();
    }
    this.records.clear();
  }

  dispose() {
    this.clear();
  }
}
